/**
 * Whiteflys — Pop-up de campanha (canto inferior esquerdo)
 *
 * Elemento custom que revela um card promocional depois de um atraso,
 * lembra a dispensa do visitante em localStorage e fecha com o X ou Esc.
 *
 * Não é um modal: não bloqueia a página nem prende o foco. É um aviso
 * complementar, então o teclado continua livre para navegar o site.
 *
 * Atributos (definidos pela seção wf-popup-evento.liquid):
 *   data-delay         atraso em ms antes de aparecer
 *   data-dismiss-days  dias que a dispensa permanece válida (0 = só a sessão)
 *   data-storage-key   chave da campanha; mudar reinicia para todo mundo
 *   data-preview       "true" no editor de tema: sempre mostra, nunca grava
 */

const PREFIX = 'wf-promo:';

/** localStorage falha em modo restrito/privado — nunca deixar isso quebrar a página. */
function readStore(key) {
  try {
    return window.localStorage.getItem(PREFIX + key);
  } catch {
    return null;
  }
}

function writeStore(key, value) {
  try {
    window.localStorage.setItem(PREFIX + key, value);
  } catch {
    /* sem persistência: o pop-up simplesmente volta na próxima visita */
  }
}

class WfPromoPopup extends HTMLElement {
  connectedCallback() {
    this.card = this.querySelector('[data-promo-card]');
    this.closeButton = this.querySelector('[data-promo-close]');
    if (!this.card) return;

    this.storageKey = this.dataset.storageKey || 'default';
    this.dismissDays = parseInt(this.dataset.dismissDays || '0', 10);
    this.isPreview = this.dataset.preview === 'true';

    this.handleClose = this.handleClose.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);

    if (!this.isPreview && this.isDismissed()) {
      this.remove();
      return;
    }

    this.closeButton?.addEventListener('click', this.handleClose);
    document.addEventListener('keydown', this.handleKeydown);

    const delay = parseInt(this.dataset.delay || '0', 10);
    this.timer = window.setTimeout(() => this.reveal(), Number.isNaN(delay) ? 0 : delay);
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
    document.removeEventListener('keydown', this.handleKeydown);
  }

  isDismissed() {
    const stored = readStore(this.storageKey);
    if (!stored) return false;
    // "session" = dispensado nesta aba; qualquer outro valor é um timestamp de validade.
    if (stored === 'session') return sessionStorage.getItem(PREFIX + this.storageKey) === '1';
    const expiry = parseInt(stored, 10);
    return !Number.isNaN(expiry) && Date.now() < expiry;
  }

  reveal() {
    this.hidden = false;
    // Dois frames: garante que o estado inicial seja pintado antes da transição.
    requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-visible')));
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && !this.hidden) this.handleClose();
  }

  handleClose() {
    this.classList.remove('is-visible');
    if (!this.isPreview) this.persistDismissal();

    const done = () => this.remove();
    // Espera a transição de saída, com rede de segurança caso ela não dispare.
    this.card.addEventListener('transitionend', done, { once: true });
    window.setTimeout(done, 500);
  }

  persistDismissal() {
    if (this.dismissDays > 0) {
      writeStore(this.storageKey, String(Date.now() + this.dismissDays * 86400000));
      return;
    }
    writeStore(this.storageKey, 'session');
    try {
      sessionStorage.setItem(PREFIX + this.storageKey, '1');
    } catch {
      /* ignorado */
    }
  }
}

if (!customElements.get('wf-promo-popup')) {
  customElements.define('wf-promo-popup', WfPromoPopup);
}
